import React, { useState, useEffect } from 'react';
import { CreditCard, Shield, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { uploadToDatabase } from '../firebase/databaseUpload';

// Declare Razorpay globally
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PaymentSectionProps {
  selectedPages: number[];
  totalCost: number;
  copies?: number;
  selectedFile?: File;
  selectedPrinter?: string;
  onPayment: (paymentData: any) => void;
}

export const PaymentSection: React.FC<PaymentSectionProps> = ({ 
  selectedPages, 
  totalCost, 
  copies = 1,
  selectedFile,
  selectedPrinter = 'hp_1',
  onPayment 
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isRazorpayReady, setIsRazorpayReady] = useState<boolean>(false);
  const [extractedFile, setExtractedFile] = useState<File | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string>('');

  const loadRazorpayScript = async (): Promise<boolean> => {
    if (window.Razorpay) {
      return true;
    }

    const existingScript = document.getElementById('razorpay-script') as HTMLScriptElement | null;

    if (existingScript) {
      return new Promise((resolve) => {
        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (window.Razorpay) {
            window.clearInterval(timer);
            resolve(true);
            return;
          }

          if (attempts >= 20) {
            window.clearInterval(timer);
            resolve(false);
          }
        }, 150);
      });
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;

      script.onload = () => resolve(!!window.Razorpay);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  // Function to create preview of selected pages
  const createExtractedFile = async () => {
    if (!selectedFile || selectedPages.length === 0 || selectedFile.type !== 'application/pdf') {
      return;
    }

    try {
      setLoading(true);
      
      // Import PDF processing
      const { PDFDocument } = await import('pdf-lib');
      
      const arrayBuffer = await selectedFile.arrayBuffer();
      const originalPdf = await PDFDocument.load(arrayBuffer);
      const newPdf = await PDFDocument.create();
      
      // Copy selected pages
      for (const pageNumber of selectedPages) {
        const pageIndex = pageNumber - 1;
        if (pageIndex >= 0 && pageIndex < originalPdf.getPageCount()) {
          const [copiedPage] = await newPdf.copyPages(originalPdf, [pageIndex]);
          newPdf.addPage(copiedPage);
        }
      }
      
      const pdfBytes = await newPdf.save();
      const extractedPdf = new File([new Uint8Array(pdfBytes)], `${selectedFile.name.replace('.pdf', '')}_pages_${selectedPages.join('-')}.pdf`, {
        type: 'application/pdf'
      });
      
      const url = URL.createObjectURL(extractedPdf);
      setExtractedFile(extractedPdf);
      setDownloadUrl(url);
      
    } catch (error) {
      console.error('Error creating extracted file:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create extracted file when pages are selected
  useEffect(() => {
    if (selectedPages.length > 0 && selectedFile?.type === 'application/pdf') {
      createExtractedFile();
    }
    
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [selectedPages, selectedFile]);

  const downloadExtractedPDF = () => {
    if (downloadUrl && extractedFile) {
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = extractedFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  useEffect(() => {
    const initRazorpay = async () => {
      const ready = await loadRazorpayScript();
      setIsRazorpayReady(ready);
    };

    initRazorpay();
  }, []);

  const handleRazorpayPayment = async () => {
    if (!window.Razorpay) {
      const ready = await loadRazorpayScript();
      setIsRazorpayReady(ready);
      if (!ready) {
        alert('Payment system not loaded. Please check your internet and try again.');
        return;
      }
    }

    setLoading(true);

    try {
      // 1. Create Order via Backend
      const backendUrl = 'https://printapi.krashakinnovativesolution.com';
      const orderRes = await fetch(`${backendUrl}/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          pagesCount: selectedPages.length, 
          copies: copies 
        })
      });
      
      if (!orderRes.ok) {
        throw new Error('Failed to create order. Please try again.');
      }
      
      const orderData = await orderRes.json();

      // 2. Open Razorpay Checkout
      const rzpOptionsKey = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_S7mOpN7E51FKFH';
      console.log('Using Razorpay Key:', rzpOptionsKey);
      console.log('Using Order ID:', orderData.id);
      
      const options = {
        key: rzpOptionsKey,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.id,
        name: 'PrintPro Services',
        description: `Print ${selectedPages?.length || 0} pages`,
        image: '/logo192.png',
        handler: async (response: any) => {
          if (selectedFile) {
            try {
              console.log('Payment successful in frontend, processing file...');
              
              // 3. Upload to Firebase (Status will be 'payment_pending')
              const uploadResult = await uploadToDatabase(selectedFile, {
                name: 'Customer',
                email: '',
                phone: ''
              }, selectedPages, totalCost, selectedPrinter, copies);
              
              if (uploadResult.success) {
                // 4. Verify payment via backend and unlock job
                const verifyRes = await fetch(`${backendUrl}/verify-payment`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    firebaseJobId: uploadResult.jobId
                  })
                });

                const verifyData = await verifyRes.json();

                if (verifyData.success) {
                  onPayment({
                    ...response,
                    uploadResult: uploadResult,
                    amount: totalCost,
                    selectedPages: selectedPages,
                    method: 'razorpay'
                  });
                } else {
                  onPayment({
                    ...response,
                    uploadError: 'Payment verification failed. Signatures mismatch.',
                    amount: totalCost,
                    selectedPages: selectedPages,
                    method: 'razorpay'
                  });
                }
              } else {
                onPayment({
                  ...response,
                  uploadError: uploadResult.error || 'Upload failed',
                  amount: totalCost,
                  selectedPages: selectedPages,
                  method: 'razorpay'
                });
              }
            } catch (error) {
              console.error('Upload error:', error);
              onPayment({
                ...response,
                uploadError: 'Processing failed. Please contact support.',
                amount: totalCost,
                selectedPages: selectedPages,
                method: 'razorpay'
              });
            }
          }
          setLoading(false);
        },
        prefill: {
          name: 'Customer',
          email: 'customer@example.com',
          contact: '9999999999'
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error starting payment:', error);
      alert('Error initializing payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      animation: 'float 6s ease-in-out infinite, fadeIn 0.5s ease',
      animationDelay: '0.4s',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      marginTop: '2rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '2rem',
        boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1), 0 10px 20px -5px rgba(0,0,0,0.04)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            width: '64px', height: '64px', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1rem', boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.3)'
          }}>
            <Shield style={{ width: '32px', height: '32px', color: '#d97706' }} />
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '0 0 0.25rem 0' }}>Secure Checkout</h3>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0, fontWeight: '500' }}>Powered by Razorpay</p>
        </div>

        <button
          onClick={handleRazorpayPayment}
          disabled={loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile}
          style={{
            background: (loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile) ? '#f1f5f9' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: (loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile) ? '#94a3b8' : 'white',
            border: (loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile) ? '1px solid #e2e8f0' : 'none',
            padding: '16px 32px',
            borderRadius: '16px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: (loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: (loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile) ? 'none' : '0 10px 25px -5px rgba(37, 99, 235, 0.4)',
            width: '100%',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseOver={(e) => { 
            if (!(loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile)) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 15px 30px -5px rgba(37, 99, 235, 0.5)';
            }
          }}
          onMouseOut={(e) => {
            if (!(loading || !isRazorpayReady || selectedPages.length === 0 || !selectedFile)) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(37, 99, 235, 0.4)';
            }
          }}
        >
          {loading ? (
            <>
              <div style={{ width: '24px', height: '24px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <span>Processing...</span>
            </>
          ) : !isRazorpayReady ? (
            <>
              <div style={{ width: '20px', height: '20px', border: '2px solid #cbd5e1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <span>Loading Payment...</span>
            </>
          ) : (
            <>
              <CreditCard style={{ width: '24px', height: '24px' }} />
              <span>Pay securely ₹{totalCost}</span>
            </>
          )}
        </button>
      </div>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}
      </style>
    </div>
  );
};
